import React, { useState, useEffect, useMemo } from 'react'
import { useGardenContext } from '../context/GardenContext'
import { allPlants as plants } from '../data/plants-complete'
import { getRegionalNames } from '../data/simpleRegionalNames'
import { getEmojiByName } from '../data/plantEmojis'
import GardenList from '../components/GardenList'
import ZoneFilter, { plantSurvivesInZone } from '../components/ZoneFilter'

// US States and territories for regional names
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'GU', name: 'Guam' },
  { code: 'AS', name: 'American Samoa' }
]

const GardenDesigner: React.FC = () => {
  const {
    gardens,
    selectedGardenId,
    selectedGarden,
    setSelectedGardenId,
    createGarden,
    updateGarden,
    updateBed,
    addBed,
    removeBed,
    plantInBed,
    clearBed,
    getGardenStats,
    myPlants,
    addToMyPlants,
    removeFromMyPlants,
    isInMyPlants
  } = useGardenContext()

  const [selectedPlant, setSelectedPlant] = useState<string | null>(null)
  const [showGridSettings, setShowGridSettings] = useState(false)
  const [gridWidth, setGridWidth] = useState(20)
  const [gridHeight, setGridHeight] = useState(20)
  const [zoom, setZoom] = useState(100) // Zoom percentage
  const [showPlantDetail, setShowPlantDetail] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('')
  const [selectedZone, setSelectedZone] = useState<string>('')
  const [selectedState, setSelectedState] = useState<string>('')
  const [allowOverlap, setAllowOverlap] = useState<boolean>(false)
  const [collectionPlants, setCollectionPlants] = useState<string[]>([])
  const [showCollectionBanner, setShowCollectionBanner] = useState(false)

  // Load selected collection from localStorage on mount
  useEffect(() => {
    const savedCollection = localStorage.getItem('gardenPlannerSelectedCollection')
    if (savedCollection) {
      try {
        const plantIds = JSON.parse(savedCollection)
        setCollectionPlants(plantIds)
        setShowCollectionBanner(true)
        // Clear it so it doesn't show again on next visit
        localStorage.removeItem('gardenPlannerSelectedCollection')
      } catch (e) {
        console.error('Failed to parse selected collection')
      }
    }
  }, [])

  // Define vegetable subcategories based on plant name patterns
  const vegetableSubcategories = {
    'lettuce': 'Lettuce',
    'spinach': 'Spinach',
    'kale': 'Kale & Collards',
    'chard': 'Chard',
    'cabbage': 'Cabbage & Brussels',
    'broccoli': 'Broccoli & Cauliflower',
    'tomato': 'Tomatoes',
    'pepper': 'Peppers',
    'cucumber': 'Cucumbers',
    'squash': 'Squash & Pumpkins',
    'zucchini': 'Zucchini',
    'bean': 'Beans',
    'pea': 'Peas',
    'carrot': 'Carrots',
    'beet': 'Beets',
    'radish': 'Radishes',
    'turnip': 'Turnips & Rutabagas',
    'potato': 'Potatoes',
    'sweetpotato': 'Sweet Potatoes',
    'onion': 'Onions & Garlic',
    'corn': 'Corn',
    'eggplant': 'Eggplants',
    'okra': 'Okra',
    'melon': 'Melons',
    'pumpkin': 'Pumpkins',
    'asparagus': 'Asparagus',
    'artichoke': 'Artichokes',
    'celery': 'Celery',
    'leek': 'Leeks',
    'shallot': 'Shallots',
    'bok choy': 'Asian Greens',
    'arugula': 'Arugula',
    'mustard': 'Mustard Greens',
    'collard': 'Collards',
    'cress': 'Cress',
    'endive': 'Endive & Chicory',
    'escarole': 'Escarole',
    'mizuna': 'Asian Greens',
    'tatsoi': 'Asian Greens',
    'pac choi': 'Asian Greens',
    'radicchio': 'Chicory',
    'sorrel': 'Sorrel',
    'watercress': 'Watercress',
    'parsnip': 'Parsnips',
    'salsify': 'Root Vegetables',
    'horseradish': 'Root Vegetables',
    'ginger': 'Root Vegetables',
    'turmeric': 'Root Vegetables',
  }

  // Get subcategory for a plant
  const getPlantSubcategory = (plant: typeof plants[0]): string => {
    const nameLower = plant.name.toLowerCase()
    for (const [key, value] of Object.entries(vegetableSubcategories)) {
      if (nameLower.includes(key)) return value
    }
    return 'Other Vegetables'
  }

  // Get unique subcategories for selected category
  const getSubcategories = (category: string): string[] => {
    if (category !== 'vegetable') return []
    const subs = new Set<string>()
    plants.filter(p => p.category === 'vegetable').forEach(p => {
      subs.add(getPlantSubcategory(p))
    })
    return Array.from(subs).sort()
  }

  // Filter plants by category, subcategory, and zone
  const filteredPlants = useMemo(() => {
    if (!selectedCategory) return []
    
    return plants.filter(p => {
      // Check category
      let categoryMatch = false
      if (selectedCategory === 'tree') {
        categoryMatch = p.category === 'tree' || p.category === 'nut'
      } else if (selectedCategory === 'vegetable' && selectedSubcategory) {
        categoryMatch = p.category === 'vegetable' && getPlantSubcategory(p) === selectedSubcategory
      } else {
        categoryMatch = p.category === selectedCategory
      }
      
      // Check zone
      const zoneMatch = p.hardinessZones ? plantSurvivesInZone(p.hardinessZones, selectedZone) : true
      
      return categoryMatch && zoneMatch
    })
  }, [selectedCategory, selectedSubcategory, selectedZone, plants])

  // Grid settings with zoom
  // Each grid square = 2 inches
  // At 100%: 20px = 2 inches, so 120px = 1 foot (12 inches)
  const BASE_PIXELS_PER_2_INCHES = 20  // 20px = 2 inches at 100%
  const PIXELS_PER_2_INCHES = BASE_PIXELS_PER_2_INCHES * (zoom / 100)
  const PIXELS_PER_FOOT = PIXELS_PER_2_INCHES * 6  // 6 × 2 inches = 1 foot
  const GRID_PIXELS_WIDTH = gridWidth * PIXELS_PER_FOOT
  const GRID_PIXELS_HEIGHT = gridHeight * PIXELS_PER_FOOT
  
  // Zoom limits
  const MIN_ZOOM = 25
  const MAX_ZOOM = 400
  
  // Handle scroll wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -10 : 10
    setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)))
  }
  
  // Rotate a plant bed 90 degrees
  const handleRotateBed = (bedId: string) => {
    if (!selectedGarden) return
    const bed = selectedGarden.beds.find(b => b.id === bedId)
    if (!bed) return
    
    // Calculate center point
    const centerX = bed.x + bed.width / 2
    const centerY = bed.y + bed.height / 2
    
    // Swap width and height
    const newWidth = bed.height
    const newHeight = bed.width
    
    // Adjust x,y to keep center in same position
    const newX = centerX - newWidth / 2
    const newY = centerY - newHeight / 2
    
    // Increment rotation (0 → 90 → 180 → 270 → 0)
    const currentRotation = bed.rotation || 0
    const newRotation = (currentRotation + 90) % 360
    
    updateBed(selectedGarden.id, bedId, {
      x: Math.round(newX),
      y: Math.round(newY),
      width: newWidth,
      height: newHeight,
      rotation: newRotation
    })
  }
  
  // Drag and drop state
  const [draggedBed, setDraggedBed] = useState<string | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  
  const handleMouseDown = (e: React.MouseEvent, bedId: string) => {
    e.stopPropagation()
    if (!selectedGarden) return
    
    const bed = selectedGarden.beds.find(b => b.id === bedId)
    if (!bed) return
    
    setDraggedBed(bedId)
    
    // Calculate offset from bed's top-left corner
    const rect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
  }
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggedBed || !selectedGarden) return
    
    const bed = selectedGarden.beds.find(b => b.id === draggedBed)
    if (!bed) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const newX = (e.clientX - rect.left - dragOffset.x) / PIXELS_PER_FOOT
    const newY = (e.clientY - rect.top - dragOffset.y) / PIXELS_PER_FOOT
    
    // Check bounds
    if (newX < 0 || newX + bed.width > gridWidth || newY < 0 || newY + bed.height > gridHeight) {
      return // Don't move if out of bounds
    }
    
    // Check for overlap with other plants
    const plant = bed.plantId ? plants.find(p => p.id === bed.plantId) : null
    const wouldOverlap = selectedGarden.beds.some(otherBed => {
      if (otherBed.id === draggedBed) return false
      const otherPlant = otherBed.plantId ? plants.find(p => p.id === otherBed.plantId) : null
      
      // Create temporary bed object at new position
      const tempBed = { ...bed, x: newX, y: newY }
      return ovalsOverlap(tempBed, otherBed, plant, otherPlant)
    })
    
    if (wouldOverlap && !allowOverlap) {
      return // Don't move if would overlap (unless overlap is allowed)
    }
    
    // Update bed position
    updateBed(selectedGarden.id, draggedBed, {
      x: Math.round(newX * 10) / 10, // Round to 1 decimal for smoother dragging
      y: Math.round(newY * 10) / 10
    })
  }
  
  const handleMouseUp = () => {
    setDraggedBed(null)
  }

  // Print garden layout
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Please allow popups to print the garden plan')
      return
    }
    
    // Force cache refresh with timestamp
    const cacheBuster = Date.now()

    const plantList = getPrintPlantList()
    const stats = selectedGarden ? getGardenStats(selectedGarden.id) : null

    // Generate print HTML
    const printHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>${selectedGarden?.name || 'Garden Plan'} - Muddy Boots Garden Planner</title>
  <style>
    @page { margin: 0.5in; size: auto; }
    * { box-sizing: border-box; }
    body { 
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 0; 
      padding: 20px; 
      background: white;
      color: #333;
    }
    .header { 
      text-align: center; 
      margin-bottom: 20px; 
      border-bottom: 2px solid #333; 
      padding-bottom: 10px; 
    }
    .header h1 { margin: 0 0 10px 0; font-size: 24px; }
    .header p { margin: 0; color: #666; font-size: 14px; }
    .grid-container { 
      display: flex; 
      justify-content: center; 
      margin-bottom: 30px;
    }
    .grid { 
      background: #f5f5f0;
      background-image: 
        linear-gradient(#ccc 1px, transparent 1px),
        linear-gradient(90deg, #ccc 1px, transparent 1px);
      background-size: 40px 40px;
      position: relative;
      border: 2px solid #333;
      width: 600px;
      height: 600px;
    }
    .plant-bed {
      position: absolute;
      display: flex;
      flex-direction: column;
      alignItems: center;
      justifyContent: center;
      font-size: 10px;
      text-align: center;
      overflow: hidden;
    }
    .plant-oval {
      position: absolute;
      background: rgba(76, 175, 80, 0.3);
      border: 2px solid #4caf50;
      border-radius: 50%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .plant-oval .emoji { font-size: 14px; }
    .plant-oval .name { font-weight: bold; color: #2e7d32; font-size: 9px; }
    h2 { font-size: 18px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
    table { width: 100%; border-collapse: collapse; margin-top: 15px; }
    th { background: #f5f5f5; border-bottom: 2px solid #333; text-align: left; padding: 8px; font-size: 12px; }
    td { padding: 8px; font-size: 12px; border-bottom: 1px solid #ddd; }
    .count { text-align: center; font-weight: bold; color: #2e7d32; }
    .summary { margin-top: 30px; padding: 15px; background: #f9f9f9; border-radius: 8px; }
    .summary h2 { margin: 0 0 10px 0; font-size: 16px; }
    .summary-grid { display: flex; gap: 30px; flex-wrap: wrap; }
    .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #999; border-top: 1px solid #ddd; padding-top: 10px; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>🌱 ${selectedGarden?.name || 'My Garden'}</h1>
    <p>Garden Plan • ${selectedGarden?.width || 0}' × ${selectedGarden?.height || 0}' • ${new Date().toLocaleDateString()}</p>
  </div>

  <div class="grid-container">
    <div class="grid" style="width: 600px; height: ${Math.min(600, (selectedGarden?.height || 20) * 30)}px;">
      ${selectedGarden?.beds.map(bed => {
        const plant = plants.find(p => p.id === bed.plantId)
        if (!plant) return ''
        const scaleX = 600 / (selectedGarden?.width || 20)
        const scaleY = Math.min(600, (selectedGarden?.height || 20) * 30) / (selectedGarden?.height || 20)
        // Calculate oval size based on plant spacing (convert inches to feet, then to pixels)
        const plantSpacingFt = plant.spacing / 12
        const rowSpacingFt = plant.rowSpacing / 12
        const ovalWidth = plantSpacingFt * scaleX
        const ovalHeight = rowSpacingFt * scaleY
        const bedWidthPx = bed.width * scaleX
        const bedHeightPx = bed.height * scaleY
        const centerX = bedWidthPx / 2
        const centerY = bedHeightPx / 2
        return `<div class="plant-bed" style="left: ${bed.x * scaleX}px; top: ${bed.y * scaleY}px; width: ${bedWidthPx}px; height: ${bedHeightPx}px;">
          <div class="plant-oval" style="width: ${ovalWidth}px; height: ${ovalHeight}px; left: ${centerX - ovalWidth/2}px; top: ${centerY - ovalHeight/2}px;">
            <span class="emoji">${getEmojiByName(plant.name)}</span>
            <span class="name">${plant.name}</span>
          </div>
        </div>`
      }).join('') || ''}
    </div>
  </div>

  <h2>📋 Plant List</h2>
  ${plantList.length === 0 ? '<p style="color: #666; font-style: italic;">No plants in this garden yet.</p>' : `
  <table>
    <thead>
      <tr>
        <th>Plant</th>
        <th style="text-align: center;">Beds</th>
        <th style="text-align: center;">Total Plants</th>
        <th>Spacing</th>
        <th>Harvest</th>
      </tr>
    </thead>
    <tbody>
      ${plantList.map(({ plant, count, beds }) => `
      <tr>
        <td><strong>${getEmojiByName(plant.name)} ${plant.name}</strong>${plant.scientificName ? `<div style="font-size: 10px; color: #666; font-style: italic;">${plant.scientificName}</div>` : ''}</td>
        <td style="text-align: center;">${beds}</td>
        <td class="count">${count}</td>
        <td>${plant.spacing}" × ${plant.rowSpacing}"</td>
        <td>${plant.daysToHarvest} days</td>
      </tr>
      `).join('')}
    </tbody>
  </table>
  `}

  ${stats ? `
  <div class="summary">
    <h2>📊 Garden Summary</h2>
    <div class="summary-grid">
      <div><strong>Total Beds:</strong> ${stats.totalBeds}</div>
      <div><strong>Planted:</strong> ${stats.plantedBeds}</div>
      <div><strong>Plant Types:</strong> ${stats.uniquePlants}</div>
      <div><strong>Utilization:</strong> ${Math.round(stats.utilization)}%</div>
    </div>
  </div>
  ` : ''}

  <div class="footer">
    Printed from Muddy Boots Garden Planner • ${new Date().toLocaleString()}
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print()
        setTimeout(function() { window.close() }, 500)
      }, 250)
    }
  </script>
</body>
</html>`

    printWindow.document.write(printHTML)
    printWindow.document.close()
  }

  // Generate print-friendly plant list
  const getPrintPlantList = () => {
    if (!selectedGarden) return []
    const plantCounts: Record<string, { plant: typeof plants[0], count: number, beds: number }> = {}
    
    selectedGarden.beds.forEach(bed => {
      if (!bed.plantId) return
      const plant = plants.find(p => p.id === bed.plantId)
      if (!plant) return
      
      if (!plantCounts[plant.id]) {
        plantCounts[plant.id] = { plant, count: 0, beds: 0 }
      }
      
      // Calculate plants per bed
      const plantsPerRow = Math.floor((bed.width * 12) / plant.spacing)
      const plantsPerColumn = Math.floor((bed.height * 12) / plant.rowSpacing)
      const totalPlants = plantsPerRow * plantsPerColumn
      
      plantCounts[plant.id].count += totalPlants
      plantCounts[plant.id].beds += 1
    })
    
    return Object.values(plantCounts).sort((a, b) => a.plant.name.localeCompare(b.plant.name))
  }

  // Auto-create a garden if none exists
  useEffect(() => {
    if (gardens.length === 0 && !selectedGardenId) {
      const newGardenId = createGarden('My First Garden', 20, 20)
      setSelectedGardenId(newGardenId)
    } else if (gardens.length > 0 && !selectedGardenId) {
      // Auto-select first garden
      setSelectedGardenId(gardens[0].id)
    }
  }, [gardens.length, selectedGardenId, createGarden, setSelectedGardenId, gardens])

  // Update grid size when selected garden changes
  useEffect(() => {
    if (selectedGarden) {
      setGridWidth(selectedGarden.width)
      setGridHeight(selectedGarden.height)
    }
  }, [selectedGarden])

  // Check if two ovals overlap (allow touching but not overlapping)
  const ovalsOverlap = (bed1: any, bed2: any, plant1: any, plant2: any) => {
    // If overlap is allowed, always return false (no collision)
    if (allowOverlap) return false
    
    // Get centers
    const centerX1 = bed1.x + bed1.width / 2
    const centerY1 = bed1.y + bed1.height / 2
    const centerX2 = bed2.x + bed2.width / 2
    const centerY2 = bed2.y + bed2.height / 2
    
    // Get oval radii (half of spacing dimensions in feet)
    // Use the actual spacing dimensions, not min
    const radiusX1 = (plant1?.spacing || 12) / 12 / 2
    const radiusY1 = (plant1?.rowSpacing || 12) / 12 / 2
    const radiusX2 = (plant2?.spacing || 12) / 12 / 2
    const radiusY2 = (plant2?.rowSpacing || 12) / 12 / 2
    
    // Check if ovals overlap using ellipse overlap approximation
    // Scale the distance by the radii to check if point is inside both ellipses
    const dx = centerX1 - centerX2
    const dy = centerY1 - centerY2
    
    // Check overlap: if the sum of radii in each direction is greater than the distance
    // Allow touching (<=) but not overlapping (<)
    const overlapX = Math.abs(dx) < (radiusX1 + radiusX2)
    const overlapY = Math.abs(dy) < (radiusY1 + radiusY2)
    
    // Both X and Y must overlap for the ovals to overlap
    return overlapX && overlapY
  }

  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedGarden || !selectedPlant) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.floor((e.clientX - rect.left) / PIXELS_PER_FOOT)
    const y = Math.floor((e.clientY - rect.top) / PIXELS_PER_FOOT)
    
    // Check bounds
    if (x < 0 || x >= gridWidth || y < 0 || y >= gridHeight) return

    const plant = plants.find(p => p.id === selectedPlant)
    if (!plant) return
    
    // Calculate bed size based on plant spacing
    const width = Math.max(Math.ceil(plant.spacing / 12), 1)
    const height = Math.max(Math.ceil(plant.rowSpacing / 12), 1)
    
    // Check bounds
    if (x + width > gridWidth || y + height > gridHeight) {
      alert('Plant would extend beyond garden boundaries.')
      return
    }
    
    // Check for oval overlap with existing plants (not just grid overlap)
    const newBed = { x, y, width, height }
    const wouldOverlap = selectedGarden.beds.some(existingBed => {
      const existingPlant = existingBed.plantId 
        ? plants.find(p => p.id === existingBed.plantId)
        : null
      return ovalsOverlap(newBed, existingBed, plant, existingPlant)
    })
    
    if (wouldOverlap && !allowOverlap) {
      alert('This plant would overlap with an existing plant. Please choose a different location, rotate the plant, or enable "Allow Overlap" in settings.')
      return
    }
        
    // Create new bed and plant
    const bedId = addBed(selectedGarden.id, {
      x,
      y,
      width,
      height,
      plantId: null,
      plantDate: null
    })
    
    plantInBed(selectedGarden.id, bedId, selectedPlant)
    // Also add to My Plants for easy access in Calendar
    addToMyPlants(selectedPlant)
  }

  const handleBedClick = (e: React.MouseEvent, bedId: string) => {
    e.stopPropagation()
    if (!selectedGarden) return

    if (selectedPlant) {
      // Replace plant in existing bed
      plantInBed(selectedGarden.id, bedId, selectedPlant)
      // Also add to My Plants
      addToMyPlants(selectedPlant)
    }
    // If no plant selected, do nothing (or we could show a context menu)
  }

  const handleDeleteBed = (e: React.MouseEvent, bedId: string) => {
    e.stopPropagation()
    if (!selectedGarden) return
    
    if (confirm('Delete this plant bed?')) {
      removeBed(selectedGarden.id, bedId)
    }
  }

  const stats = selectedGarden ? getGardenStats(selectedGarden.id) : null

  return (
    <div style={{ padding: '1rem' }}>
      <div className="card">
        <h2>🎨 Garden Designer</h2>
        <p>Plan your garden layout. Select a plant, then click on the grid to place it.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem', marginTop: '1rem' }}>
        {/* Sidebar */}
        <div>
          <GardenList />

          {/* My Plants - Quick Access */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h4>🌱 My Plants ({myPlants.length})</h4>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              Quick access to your favorite plants. Click to select, × to remove.
            </p>
            {myPlants.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                No plants yet. Add from the selector below or from the Calendar page.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem', maxHeight: '200px', overflow: 'auto' }}>
                {myPlants.map(plantId => {
                  const plant = plants.find(p => p.id === plantId)
                  if (!plant) return null
                  return (
                    <div 
                      key={plantId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        background: selectedPlant === plantId ? '#e8f5e9' : '#f5f5f5',
                        border: `1px solid ${selectedPlant === plantId ? '#4caf50' : '#ddd'}`,
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedPlant(selectedPlant === plantId ? null : plantId)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '0.9rem' }}>{plant.name}</span>
                        {plant.scientificName && (
                          <span style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>
                            {plant.scientificName}
                          </span>
                        )}
                        {/* Regional Names in My Plants */}
                        {selectedState && plant.regionalNames && plant.regionalNames[selectedState] && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                            {(Array.isArray(plant.regionalNames[selectedState]) 
                              ? plant.regionalNames[selectedState] 
                              : [plant.regionalNames[selectedState]]
                            ).slice(0, 2).map((name, idx) => (
                              <span key={idx} style={{ 
                                background: '#4caf50', 
                                color: 'white', 
                                padding: '0.1rem 0.35rem', 
                                borderRadius: '10px',
                                fontSize: '0.7rem'
                              }}>
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeFromMyPlants(plantId)
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#d9534f',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          padding: '0 0.25rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {selectedGarden && stats && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h4>Garden Stats</h4>
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Total Beds:</span>
                  <strong>{stats.totalBeds}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Planted:</span>
                  <strong style={{ color: '#4caf50' }}>{stats.plantedBeds}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Empty:</span>
                  <strong style={{ color: '#999' }}>{stats.emptyBeds}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Plant Types:</span>
                  <strong>{stats.uniquePlants}</strong>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Utilization</div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    background: '#eee', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${stats.utilization}%`,
                      height: '100%',
                      background: stats.utilization > 80 ? '#4caf50' : stats.utilization > 50 ? '#ff9800' : '#f44336',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    {Math.round(stats.utilization)}% full
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Design Area */}
        <div>
          {!selectedGarden ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🌱</div>
              <h3>Select or Create a Garden</h3>
              <p style={{ color: '#666' }}>
                Choose a garden from the sidebar or create a new one to start designing
              </p>
              {gardens.length === 0 && (
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    const id = createGarden('My First Garden', 20, 20)
                    setSelectedGardenId(id)
                  }}
                  style={{ marginTop: '1rem' }}
                >
                  Create My First Garden
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Companion Planting Info */}
              {selectedPlant && (
                <div className="card" style={{ marginBottom: '1rem', background: '#f0f8ff' }}>
                  <h4>🌿 {plants.find(p => p.id === selectedPlant)?.name} Companion Info</h4>
                  {(() => {
                    const plant = plants.find(p => p.id === selectedPlant)
                    if (!plant) return null
                    return (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#4caf50' }}>✓ Good companions:</strong>{' '}
                          {plant.companions.length > 0 
                            ? plant.companions.map(c => plants.find(p => p.id === c)?.name || c).join(', ')
                            : 'None known'}
                        </div>
                        <div>
                          <strong style={{ color: '#f44336' }}>✗ Avoid planting near:</strong>{' '}
                          {plant.enemies.length > 0 
                            ? plant.enemies.map(e => plants.find(p => p.id === e)?.name || e).join(', ')
                            : 'None known'}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Collection Plants Banner */}
              {showCollectionBanner && collectionPlants.length > 0 && (
                <div className="card" style={{ marginBottom: '1rem', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '2px solid #22c55e' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#166534' }}>🎯 Your Curated Collection</h4>
                    <button 
                      onClick={() => setShowCollectionBanner(false)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        color: '#166534'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '0.75rem' }}>
                    Plants from your selected collection. Click to add to your garden!
                  </p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                    gap: '0.5rem',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {collectionPlants.map(plantId => {
                      const plant = plants.find(p => p.id === plantId)
                      if (!plant) return null
                      return (
                        <div
                          key={plantId}
                          onClick={() => setSelectedPlant(selectedPlant === plantId ? null : plantId)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: selectedPlant === plantId ? '#22c55e' : 'white',
                            color: selectedPlant === plantId ? 'white' : '#166534',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            border: `1px solid ${selectedPlant === plantId ? '#22c55e' : '#bbf7d0'}`,
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{plant.name}</div>
                          {plant.scientificName && (
                            <div style={{ fontSize: '0.75rem', opacity: 0.8, fontStyle: 'italic' }}>
                              {plant.scientificName}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Plant Selector - Two Dropdown System */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0 }}>Select a Plant to Add</h4>
                  {/* State Selector */}
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    style={{ 
                      padding: '0.35rem 0.5rem',
                      fontSize: '0.85rem',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      minWidth: '140px'
                    }}
                  >
                    <option value="">All States</option>
                    {US_STATES.map(state => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Step 1: Category Dropdown */}
                <div style={{ marginTop: '0.75rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                    Step 1: Choose Category
                  </label>
                  <select 
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value)
                      setSelectedSubcategory('') // Reset subcategory
                      setSelectedPlant(null) // Reset plant
                    }}
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem',
                      fontSize: '0.9rem',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  >
                    <option value="">-- Select Category --</option>
                    <option value="vegetable">🥬 Vegetables ({plants.filter(p => p.category === 'vegetable' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="herb">🌿 Herbs ({plants.filter(p => p.category === 'herb' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="flower">🌸 Flowers ({plants.filter(p => p.category === 'flower' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="fruit">🍎 Fruits ({plants.filter(p => p.category === 'fruit' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="berry">🍇 Berries ({plants.filter(p => p.category === 'berry' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="allium">🧅 Alliums ({plants.filter(p => p.category === 'allium' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="cucurbit">🎃 Squash & Cucurbits ({plants.filter(p => p.category === 'cucurbit' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="legume">🥜 Legumes ({plants.filter(p => p.category === 'legume' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="grain">🌾 Grains ({plants.filter(p => p.category === 'grain' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>

                    <option value="tree">🌳 Trees ({plants.filter(p => (p.category === 'tree' || p.category === 'nut') && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="mushroom">🍄 Mushrooms ({plants.filter(p => p.category === 'mushroom' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="microgreen">🌿 Microgreens ({plants.filter(p => p.category === 'microgreen' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="sprout">🌱 Sprouts ({plants.filter(p => p.category === 'sprout' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                    <option value="cover_crop">🌾 Cover Crops ({plants.filter(p => p.category === 'cover_crop' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})</option>
                  </select>
                </div>

                {/* Step 2: Zone Filter */}
                <div style={{ marginTop: '0.75rem' }}>
                  <ZoneFilter 
                    selectedZone={selectedZone}
                    onZoneChange={(zone) => {
                      setSelectedZone(zone)
                      setSelectedPlant(null)
                    }}
                  />
                </div>

                {/* Step 3: Subcategory Dropdown (only for vegetables) */}
                {selectedCategory === 'vegetable' && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                      Step 3: Choose Vegetable Type
                    </label>
                    <select 
                      value={selectedSubcategory}
                      onChange={(e) => {
                        setSelectedSubcategory(e.target.value)
                        setSelectedPlant(null)
                      }}
                      style={{ 
                        width: '100%', 
                        padding: '0.5rem',
                        fontSize: '0.9rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    >
                      <option value="">-- All Vegetables --</option>
                      {getSubcategories('vegetable').map(sub => (
                        <option key={sub} value={sub}>
                          {sub} ({plants.filter(p => p.category === 'vegetable' && getPlantSubcategory(p) === sub && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedZone))).length})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Step 3: Plant Dropdown */}
                {selectedCategory && (selectedCategory !== 'vegetable' || selectedSubcategory) && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                      {selectedCategory === 'vegetable' ? 'Step 3' : 'Step 2'}: Choose Plant ({filteredPlants.length} available)
                    </label>
                    <select 
                      key={`plant-select-${selectedCategory}-${selectedSubcategory}`}
                      value={selectedPlant || ''}
                      onChange={(e) => setSelectedPlant(e.target.value || null)}
                      style={{ 
                        width: '100%', 
                        padding: '0.5rem',
                        fontSize: '0.9rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    >
                      <option value="">-- Select Plant --</option>
                      {filteredPlants.map(plant => (
                        <option key={plant.id} value={plant.id}>
                          {plant.name}{plant.scientificName ? ` (${plant.scientificName})` : ''} — Space: {plant.spacing}" × {plant.rowSpacing}"
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Selected Plant Info */}
                {selectedPlant && (
                  <div style={{ 
                    marginTop: '0.75rem', 
                    padding: '0.75rem',
                    background: '#e8f5e9',
                    borderRadius: '8px',
                    border: '1px solid #4caf50'
                  }}>
                    <div style={{ fontWeight: 600, color: '#2e7d32' }}>
                      ✓ {plants.find(p => p.id === selectedPlant)?.name}
                    </div>
                    {(() => {
                      const plant = plants.find(p => p.id === selectedPlant)
                      return plant?.scientificName ? (
                        <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic', marginTop: '0.25rem' }}>
                          {plant.scientificName}
                        </div>
                      ) : null
                    })()}
                    
                    {/* Regional Names */}
                    {(() => {
                      const plant = plants.find(p => p.id === selectedPlant)
                      if (!plant || !selectedState || !plant.regionalNames || !plant.regionalNames[selectedState]) return null
                      const names = plant.regionalNames[selectedState]
                      const nameList = Array.isArray(names) ? names : [names]
                      return (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong style={{ fontSize: '0.8rem', color: '#2d5a27' }}>
                            📍 Also known as in {US_STATES.find(s => s.code === selectedState)?.name}:
                          </strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.25rem' }}>
                            {nameList.map((name, idx) => (
                              <span key={idx} style={{ 
                                background: '#4caf50', 
                                color: 'white', 
                                padding: '0.15rem 0.5rem', 
                                borderRadius: '12px',
                                fontSize: '0.75rem'
                              }}>
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                    
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                      Space: {plants.find(p => p.id === selectedPlant)?.spacing}" × {plants.find(p => p.id === selectedPlant)?.rowSpacing}"
                      {' | '}Harvest: {plants.find(p => p.id === selectedPlant)?.daysToHarvest} days
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      {plants.find(p => p.id === selectedPlant)?.description}
                    </div>
                  </div>
                )}
                {selectedPlant ? (
                  <p style={{ marginTop: '0.75rem', color: '#4caf50', fontWeight: 500 }}>
                    ✓ Click on the grid to plant {plants.find(p => p.id === selectedPlant)?.name} | 
                    <span style={{ color: '#666', fontWeight: 'normal' }}> Click existing plant to replace</span>
                  </p>
                ) : (
                  <p style={{ marginTop: '0.75rem', color: '#666' }}>
                    💡 Select a plant above, then click on the grid to place it
                  </p>
                )}
              </div>

              {/* Garden Grid */}
              <div className="card">
                {/* Controls Row - Moved to top left */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start', 
                  alignItems: 'center', 
                  gap: '1rem',
                  marginBottom: '1rem',
                  flexWrap: 'wrap'
                }}>
                  {/* Zoom Controls */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    background: '#f5f5f5',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px'
                  }}>
                    <span style={{ fontSize: '0.8rem', marginRight: '0.25rem' }}>Zoom:</span>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '0.85rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => setZoom(prev => Math.max(MIN_ZOOM, prev - 25))}
                      disabled={zoom <= MIN_ZOOM}
                    >
                      −
                    </button>
                    <span style={{ fontSize: '0.85rem', minWidth: '50px', textAlign: 'center' }}>
                      {zoom}%
                    </span>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '0.85rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => setZoom(prev => Math.min(MAX_ZOOM, prev + 25))}
                      disabled={zoom >= MAX_ZOOM}
                    >
                      +
                    </button>
                  </div>

                  {/* Grid Size Controls */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    background: '#f5f5f5',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px'
                  }}>
                    <span style={{ fontSize: '0.8rem' }}>Grid:</span>
                    <input 
                      type="number" 
                      min="5" 
                      max="209" 
                      value={gridWidth}
                      onChange={(e) => {
                        const newWidth = parseInt(e.target.value) || 20
                        setGridWidth(newWidth)
                        if (selectedGarden) updateGarden(selectedGarden.id, { width: newWidth })
                      }}
                      style={{ width: '60px', fontSize: '0.85rem' }}
                    />
                    <span style={{ fontSize: '0.8rem' }}>×</span>
                    <input 
                      type="number" 
                      min="5" 
                      max="209" 
                      value={gridHeight}
                      onChange={(e) => {
                        const newHeight = parseInt(e.target.value) || 20
                        setGridHeight(newHeight)
                        if (selectedGarden) updateGarden(selectedGarden.id, { height: newHeight })
                      }}
                      style={{ width: '60px', fontSize: '0.85rem' }}
                    />
                    <span style={{ fontSize: '0.8rem' }}>ft</span>
                  </div>

                  {/* Overlap Toggle */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    background: allowOverlap ? '#e8f5e9' : '#f5f5f5',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    border: allowOverlap ? '1px solid #4caf50' : '1px solid transparent'
                  }}>
                    <input
                      type="checkbox"
                      id="allowOverlap"
                      checked={allowOverlap}
                      onChange={(e) => setAllowOverlap(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label 
                      htmlFor="allowOverlap" 
                      style={{ 
                        fontSize: '0.8rem', 
                        cursor: 'pointer',
                        color: allowOverlap ? '#2e7d32' : '#666'
                      }}
                    >
                      Allow Overlap
                    </label>
                  </div>

                  {/* Print Button */}
                  <button
                    className="btn btn-secondary"
                    onClick={handlePrint}
                    style={{ 
                      fontSize: '0.85rem', 
                      padding: '0.35rem 0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem'
                    }}
                    title="Print garden layout"
                  >
                    🖨️ Print
                  </button>

                  <h4 style={{ margin: 0, marginLeft: 'auto' }}>{selectedGarden.name}</h4>
                </div>
                
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  💡 Scroll to zoom • Each grid square = 2" • Scale: {Math.round(PIXELS_PER_2_INCHES)}px per 2"
                </div>

                {/* Grid Size Controls - Old expandable section removed, now inline above */}
                {showGridSettings && (
                  <div className="card" style={{ marginBottom: '1rem', background: '#f0f8ff' }}>
                    <h4>Grid Size Settings</h4>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ fontSize: '0.85rem' }}>Width (ft)</label>
                        <input 
                          type="number" 
                          min="5" 
                          max="209" 
                          value={gridWidth}
                          onChange={(e) => {
                            const newWidth = parseInt(e.target.value) || 20
                            setGridWidth(newWidth)
                            updateGarden(selectedGarden.id, { width: newWidth })
                          }}
                          style={{ width: '70px', marginLeft: '0.5rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.85rem' }}>Height (ft)</label>
                        <input 
                          type="number" 
                          min="5" 
                          max="209" 
                          value={gridHeight}
                          onChange={(e) => {
                            const newHeight = parseInt(e.target.value) || 20
                            setGridHeight(newHeight)
                            updateGarden(selectedGarden.id, { height: newHeight })
                          }}
                          style={{ width: '70px', marginLeft: '0.5rem' }}
                        />
                      </div>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => setShowGridSettings(false)}
                        style={{ fontSize: '0.85rem' }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
                
                <div 
                  style={{
                    width: GRID_PIXELS_WIDTH,
                    height: GRID_PIXELS_HEIGHT,
                    background: '#f5f5f0',
                    backgroundImage: `
                      linear-gradient(#ddd 1px, transparent 1px),
                      linear-gradient(90deg, #ddd 1px, transparent 1px)
                    `,
                    backgroundSize: `${PIXELS_PER_2_INCHES}px ${PIXELS_PER_2_INCHES}px`,
                    position: 'relative',
                    cursor: draggedBed ? 'grabbing' : selectedPlant ? 'crosshair' : 'default',
                    margin: '0 auto',
                    overflow: 'auto',
                    userSelect: 'none'
                  }}
                  onClick={handleGridClick}
                  onWheel={handleWheel}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {selectedGarden.beds.map(bed => {
                    const plant = plants.find(p => p.id === bed.plantId)
                    const bedWidthPx = bed.width * PIXELS_PER_FOOT
                    const bedHeightPx = bed.height * PIXELS_PER_FOOT
                    const centerX = bedWidthPx / 2
                    const centerY = bedHeightPx / 2
                    
                    // Get rotation (0, 90, 180, 270)
                    const rotation = bed.rotation || 0
                    const isRotated90or270 = rotation === 90 || rotation === 270
                    
                    // Calculate oval size based on plant spacing
                    // Swap spacing dimensions if rotated 90 or 270 degrees
                    let spacingWidth, spacingHeight
                    if (plant) {
                      const plantSpacing = (plant.spacing / 12) * PIXELS_PER_FOOT
                      const rowSpacing = (plant.rowSpacing / 12) * PIXELS_PER_FOOT
                      spacingWidth = isRotated90or270 ? rowSpacing : plantSpacing
                      spacingHeight = isRotated90or270 ? plantSpacing : rowSpacing
                    } else {
                      spacingWidth = bedWidthPx * 0.8
                      spacingHeight = bedHeightPx * 0.8
                    }
                    
                    return (
                      <div
                        key={bed.id}
                        style={{
                          position: 'absolute',
                          left: bed.x * PIXELS_PER_FOOT,
                          top: bed.y * PIXELS_PER_FOOT,
                          width: bedWidthPx,
                          height: bedHeightPx,
                          cursor: draggedBed === bed.id ? 'grabbing' : 'grab',
                        }}
                        onMouseDown={(e) => handleMouseDown(e, bed.id)}
                      >
                        {/* Plant spacing oval */}
                        {plant && (
                          <div
                            style={{
                              position: 'absolute',
                              left: centerX - spacingWidth / 2,
                              top: centerY - spacingHeight / 2,
                              width: spacingWidth,
                              height: spacingHeight,
                              background: 'rgba(76, 175, 80, 0.2)',
                              border: '2px solid #4caf50',
                              borderRadius: '50%',
                              cursor: 'pointer',
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation()
                              setShowPlantDetail(bed.id)
                            }}
                            title={`${plant.name}${plant.scientificName ? ` (${plant.scientificName})` : ''} - ${isRotated90or270 ? plant.rowSpacing : plant.spacing}"×${isRotated90or270 ? plant.spacing : plant.rowSpacing}" spacing (rotated ${rotation}°) - Double-click for details`}
                          />
                        )}
                        
                        {/* Plant emoji and label */}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: bedWidthPx,
                            height: bedHeightPx,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          {/* Plant emoji */}
                          {plant && (
                            <span style={{
                              fontSize: Math.min(bedWidthPx, bedHeightPx) > 40 ? '20px' : Math.min(bedWidthPx, bedHeightPx) > 25 ? '14px' : '10px',
                              marginBottom: '2px',
                            }}>
                              {getEmojiByName(plant.name)}
                            </span>
                          )}
                          {/* Plant name */}
                          <span style={{
                            fontSize: Math.min(bedWidthPx, bedHeightPx) > 30 ? '9px' : '7px',
                            color: plant ? '#2e7d32' : '#999',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            background: plant ? 'rgba(255,255,255,0.8)' : 'transparent',
                            padding: '1px 3px',
                            borderRadius: '3px',
                            maxWidth: '90%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {plant ? plant.name : '+'}
                          </span>
                        </div>
                        
                        {/* Action buttons */}
                        {plant && (
                          <>
                            {/* Rotate button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRotateBed(bed.id)
                              }}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                left: '2px',
                                background: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                lineHeight: 1,
                                pointerEvents: 'auto'
                              }}
                              title="Rotate plant (swap spacing)"
                            >
                              ↻
                            </button>
                            {/* Delete button */}
                            <button
                              onClick={(e) => handleDeleteBed(e, bed.id)}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                lineHeight: 1,
                                pointerEvents: 'auto'
                              }}
                              title="Delete plant"
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginTop: '1rem',
                  fontSize: '0.85rem',
                  color: '#666'
                }}>
                  <span>0 ft</span>
                  <span>{Math.round(gridWidth / 2)} ft</span>
                  <span>{gridWidth} ft</span>
                </div>
              </div>

              {/* Legend */}
              <div className="card" style={{ marginTop: '1rem' }}>
                <h4>Legend & Instructions</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(76, 175, 80, 0.3)', border: '2px solid #4caf50', borderRadius: '50%' }} />
                    <span>Plant spacing oval</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: '#2196f3', borderRadius: '50%', color: 'white', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</div>
                    <span>Click ↻ to rotate</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', background: '#f44336', borderRadius: '50%', color: 'white', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</div>
                    <span>Click × to delete</span>
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f5f5f0', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <strong>How to use:</strong><br/>
                  1. Select a plant from the list above<br/>
                  2. Click on the grid to place it (multiple plants allowed per square)<br/>
                  3. <strong>Drag plants</strong> to move them around<br/>
                  4. Green ovals show actual plant spacing<br/>
                  5. Click ↻ to rotate (swap width/height spacing)<br/>
                  6. Scroll mouse wheel to zoom in/out<br/>
                  7. Click any plant for detailed info<br/>
                  8. Plants can't overlap spacing ovals
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Plant Detail Modal */}
      {showPlantDetail && selectedGarden && (() => {
        const bed = selectedGarden.beds.find(b => b.id === showPlantDetail)
        const plant = bed?.plantId ? plants.find(p => p.id === bed.plantId) : null
        if (!bed || !plant) return null
        
        const plantsPerRow = Math.floor((bed.width * 12) / plant.spacing)
        const plantsPerColumn = Math.floor((bed.height * 12) / plant.rowSpacing)
        const totalPlants = plantsPerRow * plantsPerColumn
        
        return (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowPlantDetail(null)}
          >
            <div 
              style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0 }}>🌱 {plant.name}</h3>
              {plant.scientificName && (
                <p style={{ margin: '0.25rem 0 0 0', fontStyle: 'italic', color: '#666' }}>
                  {plant.scientificName}
                </p>
              )}
              
              <div style={{ marginTop: '1rem' }}>
                <h4>Spacing Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Plant Spacing</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.spacing}"</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Row Spacing</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.rowSpacing}"</div>
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4>Bed Capacity</h4>
                <div style={{ padding: '0.75rem', background: '#e8f5e9', borderRadius: '4px', marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>Bed size:</span>
                    <strong>{bed.width}' × {bed.height}'</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>Plants per row:</span>
                    <strong>{plantsPerRow}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>Rows:</span>
                    <strong>{plantsPerColumn}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #4caf50', paddingTop: '0.25rem', marginTop: '0.25rem' }}>
                    <span>Total plants:</span>
                    <strong style={{ color: '#2e7d32', fontSize: '1.1rem' }}>{totalPlants}</strong>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {plant.description && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Description</h4>
                  <p style={{ fontSize: '0.9rem', color: '#555', marginTop: '0.5rem' }}>
                    {plant.description}
                  </p>
                </div>
              )}

              {/* Growing Information */}
              <div style={{ marginTop: '1rem' }}>
                <h4>Growing Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Days to Germination</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.daysToGermination} days</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Days to Harvest</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.daysToHarvest} days</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Sun</div>
                    <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plant.sun}</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Water</div>
                    <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plant.water}</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Soil</div>
                    <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plant.soil}</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>pH Range</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.phMin}-{plant.phMax}</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Hardiness Zones</div>
                    <div style={{ fontWeight: 'bold' }}>{plant.hardinessZones}</div>
                  </div>
                  <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Planting Season</div>
                    <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plant.plantingSeason}</div>
                  </div>
                </div>
              </div>

              {/* Size Info */}
              {(plant.height || plant.spread) && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Plant Size</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {plant.height && (
                      <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>Height</div>
                        <div style={{ fontWeight: 'bold' }}>{plant.height}</div>
                      </div>
                    )}
                    {plant.spread && (
                      <div style={{ padding: '0.5rem', background: '#f5f5f0', borderRadius: '4px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>Spread</div>
                        <div style={{ fontWeight: 'bold' }}>{plant.spread}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Family */}
              {plant.family && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Family</h4>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>{plant.family}</p>
                </div>
              )}

              {/* Companions */}
              {plant.companions && plant.companions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Companion Plants</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {plant.companions.map(companion => (
                      <span key={companion} style={{ padding: '0.25rem 0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {companion}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Enemies */}
              {plant.enemies && plant.enemies.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Avoid Planting Near</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {plant.enemies.map(enemy => (
                      <span key={enemy} style={{ padding: '0.25rem 0.5rem', background: '#ffebee', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {enemy}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Varieties */}
              {plant.varieties && plant.varieties.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Popular Varieties</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {plant.varieties.map(variety => (
                      <span key={variety} style={{ padding: '0.25rem 0.5rem', background: '#e3f2fd', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {variety}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Uses */}
              {plant.uses && plant.uses.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4>Uses</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {plant.uses.map(use => (
                      <span key={use} style={{ padding: '0.25rem 0.5rem', background: '#fff3e0', borderRadius: '4px', fontSize: '0.85rem' }}>
                        {use}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <button 
                className="btn btn-primary"
                onClick={() => setShowPlantDetail(null)}
                style={{ marginTop: '1.5rem', width: '100%' }}
              >
                Close
              </button>
            </div>
          </div>
        )
      })()}


    </div>
  )
}

export default GardenDesigner
